import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import SweetAlert from "react-bootstrap-sweetalert";
import FormMounted from "../AboutEvent/rsvp/FormMounted";
import {
  getAttendeeByIdPublic,
  checkFormResponsePublic,
  getFormByIdPublic,
  submitFormResponsePublic,
} from "../../utils/services/rsvp";
import { getUrlFilesPublic } from "../../utils/services/files";
import { validation } from "../../utils/helpers/masks/validations";
// Reusa o CSS/script do preview modal — garante que o visual do template
// no link público bate com o "Visualizar → Nova aba" do editor.
import { FORM_CSS, SSR_SELECT_SCRIPT } from "../Templates/previewDoc";

// Página pública que renderiza um template pelo ID via link direto.
// Fica FORA do CheckAuth/Layout — sem sidebar/header do admin, pra poder
// ser embedada em outras aplicações.
//
// ============================================================================
// MOCKS TEMPORÁRIOS — trocar quando backend estiver pronto
// ----------------------------------------------------------------------------
// Alternativa A do vínculo template ↔ form: no futuro, o htmlString do
// template vai conter um `<div data-form-container data-form-id="..."
// data-type-form="...">` — o parsing extrai os valores. Enquanto isso,
// hardcode aqui só pra validar o fluxo do submit ponta-a-ponta.
// ============================================================================
// Mocks temporários — trocar quando extrair do htmlString (data-form-id etc).
// FORM_ID: id do form no banco (será buscado via GET /forms/:formId).
// TYPE_FORM: tipo do form — muda estrutura do payload no POST.
// EVENT_ID: id do evento.
// PARTICIPANT_ID: só usado se TYPE_FORM !== "cadastro".
const MOCK = {
  FORM_ID: "2e499e3a-ee71-4787-ae59-e10620d5c7c5",
  TYPE_FORM: "cadastro",
  EVENT_ID: "a338dd87-646a-4277-acfd-1a54e9eced0f",
  PARTICIPANT_ID: null,
  // Gambiarra temporária pra teste — quando o backend do editor ainda não
  // embute o wrapper `<div data-form-container data-form-id="..." ...>`
  // no htmlString salvo, este flag INJETA o wrapper no HTML recebido usando
  // os MOCK.FORM_ID/TYPE_FORM acima. Colocar em `false` quando o editor
  // passar a gerar o marker corretamente.
  INJECT_CONTAINER_IF_MISSING: false,
};

// Estratégia de renderização do form dentro do template.
// - true  (portal): renderiza o htmlString inteiro e injeta o <FormMounted/>
//   via React Portal no [data-form-container]. Preserva a estrutura DOM do
//   modelo — o form fica DENTRO das divs onde o admin arrastou.
// - false (split): comportamento legado — corta o HTML em beforeHtml/afterHtml
//   e renderiza o form NO MEIO. Fica visualmente "solto" quando o container
//   está dentro de divs aninhadas (browser auto-fecha as tags abertas).
// Se der problema com portal em algum template, troque pra false pra
// voltar ao comportamento anterior.
const USE_PORTAL_STRATEGY = true;

// Mapeia attributeIds do form pros esperados pelo backend/API global.
// Chave = valor extraído do form (uniqueField ou inputName, em minúsculo).
// Valor = attributeId final enviado no payload.
const ATTR_ALIASES = {
  cellphone: "telefone",
  celular: "telefone",
};

// Extrai formId, typeForm e o HTML antes/depois do wrapper do form.
// Retorna null se não achar wrapper — nesse caso o template é considerado
// "sem form" (só HTML estático).
const parseTemplateHtml = (htmlString) => {
  if (!htmlString) return null;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    // Suporta data-form-container (formato legado) e data-form-id (editor atual).
    const container =
      doc.querySelector("[data-form-container]") ||
      doc.querySelector("[data-form-id]");
    if (!container) return null;

    const formId = container.dataset.formId || null;
    const typeForm = container.dataset.typeForm || null;

    // Esvazia o container — remove o preview estático que o editor GrapesJS
    // gera dentro do [data-form-container] (via renderToString ao arrastar
    // o bloco do form). O portal injeta o form vivo aqui depois.
    container.innerHTML = "";
    const cleanedHtml = doc.body.innerHTML;

    // Fallback (estratégia split): substitui o wrapper por um marker e divide
    // o HTML em duas partes. Só usado se USE_PORTAL_STRATEGY = false.
    const marker = "__PUBLIC_TEMPLATE_FORM_SLOT__";
    container.outerHTML = marker;
    const [beforeHtml = "", afterHtml = ""] = doc.body.innerHTML.split(marker);

    return { formId, typeForm, beforeHtml, afterHtml, cleanedHtml };
  } catch {
    return null;
  }
};

const urlTemplates = `${process.env.REACT_APP_URL_BASE}/api-admin-template`;

// Wrapper memoizado — só re-renderiza quando o `html` mudar de verdade.
// Sem isso, cada re-render do PublicTemplate re-aplicava o
// dangerouslySetInnerHTML e destruía o [data-form-container] em que o
// portal do form estava montado (o formSlotEl ficava apontando pra um
// node desconectado do DOM).
const TemplateHtmlWrapper = React.memo(
  function TemplateHtmlWrapper({ html, wrapperRef }) {
    return (
      <div ref={wrapperRef} dangerouslySetInnerHTML={{ __html: html }} />
    );
  },
  (prev, next) => prev.html === next.html && prev.wrapperRef === next.wrapperRef,
);

const PublicTemplate = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // Query string ?paxid=<uuid> — usado quando type_form != cadastro.
  // Fallback: MOCK.PARTICIPANT_ID (piloto).
  const participantIdFromUrl = searchParams.get("paxid");
  // Fallback opcional para type_form != cadastro — permite identificar o
  // participante via ?email=<email> na URL (ex.: link enviado por e-mail
  // marketing que já sabe o destinatário mas não expõe o paxid).
  const emailFromUrl = searchParams.get("email");

  // Carrega o mesmo CSS/JS que o preview modal ("Visualizar → Nova aba")
  // usa — garante paridade visual entre o preview do editor e o link público.
  useEffect(() => {
    const links = [
      "https://cdn.jsdelivr.net/npm/bootstrap@5.1.0/dist/css/bootstrap.min.css",
      "https://unpkg.com/spectre.css/dist/spectre.min.css",
    ].map((href) => {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = href;
      l.dataset.publicTemplate = "true";
      document.head.appendChild(l);
      return l;
    });
    // Script do mock do Select (SSR) — abrir/fechar via <details>.
    const script = document.createElement("script");
    script.textContent = SSR_SELECT_SCRIPT;
    script.dataset.publicTemplate = "true";
    document.body.appendChild(script);
    return () => {
      links.forEach((l) => l.remove());
      script.remove();
    };
  }, []);

  // State do template (só metadados — não usado no piloto pra rederizar HTML)
  const [template, setTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateError, setTemplateError] = useState(null);

  // formId e typeForm derivados: extraídos do htmlString se existir wrapper
  // data-form-container; senão caem no MOCK.
  const [resolvedFormId, setResolvedFormId] = useState(null);
  const [resolvedTypeForm, setResolvedTypeForm] = useState(null);
  // HTML antes/depois do wrapper do form (pra renderizar o resto do template).
  const [beforeFormHtml, setBeforeFormHtml] = useState("");
  const [afterFormHtml, setAfterFormHtml] = useState("");
  // HTML do template com o [data-form-container] esvaziado — usado pela
  // estratégia portal pra o form vivo não duplicar o preview estático.
  const [cleanedHtml, setCleanedHtml] = useState("");

  // State do form — o FormMounted lê/escreve daqui.
  const [form, setForm] = useState([]);
  const [formLoading, setFormLoading] = useState(true);
  const [formError, setFormError] = useState(null);
  // idStatusRsvp associado ao form (opcional — vem do banco). Se existir,
  // é anexado ao payload do submit pra o backend salvar junto da resposta.
  const [formIdStatusRsvp, setFormIdStatusRsvp] = useState(null);
  // Muda a cada reset — força remount do FormMounted pra limpar inputs
  // "não controlados" (ex: <input type="file">).
  const [formKey, setFormKey] = useState(0);

  // State do submit
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null); // { ok, message, participantId? }

  // Feature 2 — resposta única. Quando `true`, o template não renderiza o
  // formulário e a página mostra a tela final "Resposta já registrada".
  const [alreadyAnswered, setAlreadyAnswered] = useState(false);
  // Distingue "já tinha respondido antes" de "acabou de responder agora"
  // pra ajustar o texto da tela final (mesma tela pros dois casos).
  const [alreadyAnsweredReason, setAlreadyAnsweredReason] = useState("existing");
  // Delay pequeno antes de mostrar a mensagem — dá sensação de progresso
  // de "processando → confirmado" em vez de trocar bruscamente de tela.
  const [showAnsweredMessage, setShowAnsweredMessage] = useState(false);
  // Paxid da URL não corresponde a nenhum participante (backend retornou
  // 4xx). Só se aplica a forms NÃO-cadastro com ?paxid=<uuid>.
  const [attendeeNotFound, setAttendeeNotFound] = useState(false);
  // Enquanto true, o render bloqueia o form (mostra spinner) — evita o
  // "flash" onde o formulário aparece por um instante antes da tela
  // "Participante não localizado". Começa `true` se há paxid/email na
  // URL; vira `false` quando a validação termina (local ou backend).
  const [attendeeChecking, setAttendeeChecking] = useState(
    Boolean(participantIdFromUrl || emailFromUrl),
  );
  // Grupos do participante identificado (retornados pelo attendee lookup).
  // `null`  = ainda não identificado (esconde colunas com groupIds).
  // `[]`    = identificado, sem grupos (só colunas sem groupIds aparecem).
  // `[...]` = identificado, filtra colunas por interseção com `groupIds`.
  const [attendeeGroupIds, setAttendeeGroupIds] = useState(null);

  useEffect(() => {
    if (!alreadyAnswered) {
      setShowAnsweredMessage(false);
      return;
    }
    const t = setTimeout(() => setShowAnsweredMessage(true), 800);
    return () => clearTimeout(t);
  }, [alreadyAnswered]);

  // Portal do form — elemento-alvo (o [data-form-container] localizado no
  // DOM). Usa callback ref pra capturar o wrapper + useEffect pra re-query
  // sempre que o cleanedHtml muda (o innerHTML é substituído nesse caso).
  // Só usado quando USE_PORTAL_STRATEGY = true.
  const [templateWrapperNode, setTemplateWrapperNode] = useState(null);
  const [formSlotEl, setFormSlotEl] = useState(null);

  const templateWrapperRefCallback = React.useCallback((node) => {
    setTemplateWrapperNode(node);
  }, []);

  useEffect(() => {
    if (!USE_PORTAL_STRATEGY) return;
    if (!templateWrapperNode || !cleanedHtml) {
      setFormSlotEl(null);
      return;
    }
    const el = templateWrapperNode.querySelector(
      "[data-form-container], [data-form-id]",
    );
    setFormSlotEl(el || null);
  }, [templateWrapperNode, cleanedHtml]);

  // Aplica a regra de segregação por grupos:
  //   - coluna sem `groupIds` → sempre visível (default);
  //   - coluna com `groupIds` → visível só se o participante identificado
  //     estiver em pelo menos um dos grupos exigidos;
  //   - `attendeeGroupIds === null` (não identificado) → esconde restritas.
  // Cadastro (inscrição) nunca filtra — não há participante ainda.
  const visibleForm = useMemo(() => {
    if (resolvedTypeForm === "cadastro") return form;
    return form
      .map((row) => ({
        ...row,
        cols: row.cols.filter((col) => {
          const groupIds = col.attributes?.groupIds;
          if (!Array.isArray(groupIds) || groupIds.length === 0) return true;
          if (!Array.isArray(attendeeGroupIds)) return false;
          return groupIds.some((g) => attendeeGroupIds.includes(g));
        }),
      }))
      .filter((row) => row.cols.length > 0);
  }, [form, attendeeGroupIds, resolvedTypeForm]);

  // Helper: zera value/errorMessage de cada campo (usado no fetch inicial
  // e após submit bem-sucedido).
  const cleanForm = (formArray) =>
    formArray.map((row) => ({
      ...row,
      cols: row.cols.map((col) => ({
        ...col,
        attributes: {
          ...col.attributes,
          value: undefined,
          errorMessage: "",
          useRawErrorMessage: false,
        },
      })),
    }));

  // Assim que o template chegar, parseia o htmlString procurando o wrapper
  // data-form-container e divide o HTML em beforeHtml/afterHtml pra
  // renderizar o resto do template (header, seções, etc) em torno do form.
  useEffect(() => {
    if (!template) return;
    const parsed = parseTemplateHtml(template.htmlString);
    if (parsed?.formId) {
      setResolvedFormId(parsed.formId);
      setResolvedTypeForm(parsed.typeForm || MOCK.TYPE_FORM);
      setBeforeFormHtml(parsed.beforeHtml);
      setAfterFormHtml(parsed.afterHtml);
      setCleanedHtml(parsed.cleanedHtml || "");
    } else if (MOCK.INJECT_CONTAINER_IF_MISSING) {
      // eslint-disable-next-line no-console
      console.warn(
        "[PublicTemplate] htmlString sem [data-form-container] — usando MOCK.FORM_ID/TYPE_FORM.",
      );
      setResolvedFormId(MOCK.FORM_ID);
      setResolvedTypeForm(MOCK.TYPE_FORM);
      // No modo MOCK, NÃO renderizamos o htmlString porque ele já contém
      // o form estático — mostrar junto duplicaria os campos. Renderiza
      // só o FormMounted vivo.
      setBeforeFormHtml("");
      setAfterFormHtml("");
      setCleanedHtml("");
    } else {
      // Sem wrapper e sem fallback: template sem form (ex: página informativa).
      setFormLoading(false);
    }
  }, [template]);

  // Busca a estrutura do form via GET /forms/:formId (público).
  useEffect(() => {
    if (!resolvedFormId) return;
    let cancelled = false;
    setFormLoading(true);
    setFormError(null);
    getFormByIdPublic({ formId: resolvedFormId }).then((res) => {
      if (cancelled) return;
      if (!res || res.status >= 400) {
        setFormError(
          res?.status === 401
            ? "Endpoint de forms exige autenticação — precisa ser público."
            : "Não foi possível carregar o formulário.",
        );
        setFormLoading(false);
        return;
      }
      const body = res?.data;
      const formData =
        body?.data?.data || body?.data?.form || body?.data || body;
      const parsed =
        typeof formData?.json === "string"
          ? JSON.parse(formData.json)
          : formData?.json || formData;
      if (Array.isArray(parsed)) {
        // Extrai idStatusRsvp do item de meta (se existir) e remove do
        // array antes de renderizar — assim FormMounted e outros consumers
        // continuam iterando só rows com `cols` como antes.
        const metaItem = parsed.find((item) => item && item._meta);
        const idStatusRsvp =
          metaItem?._meta?.idStatusRsvp ||
          formData?.idStatusRsvp ||
          formData?.id_status_rsvp ||
          formData?.statusRsvp?.id ||
          null;
        setFormIdStatusRsvp(idStatusRsvp);
        const rowsOnly = parsed.filter(
          (row) => row && Array.isArray(row.cols),
        );
        // Limpa `value` e `errorMessage` — o form vem do banco como "molde"
        // e não deve trazer respostas/erros de estado anterior.
        setForm(cleanForm(rowsOnly));
      } else {
        setFormError("Estrutura do form inválida na resposta.");
      }
      setFormLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [resolvedFormId]);

  // Pré-preenche Nome/Email do attendee quando o form NÃO é cadastro e
  // vem ?paxid=<uuid> OU ?email=<email> na URL. Os campos ficam readOnly.
  // Envia eventId junto (o backend valida se o participante pertence ao
  // evento — retorna 404 se não pertencer).
  //
  // Endpoint público:
  //   GET /attendees/:id[?eventId=...]
  //   GET /attendees?email=...[&eventId=...]
  useEffect(() => {
    if (!resolvedTypeForm || resolvedTypeForm === "cadastro") return;
    if (form.length === 0) return;
    // Precisa de pelo menos um identificador via URL.
    if (!participantIdFromUrl && !emailFromUrl) return;

    // Validação de formato antes de chamar o backend — evita 404 e
    // padroniza a resposta pro usuário (mesma tela de "não localizado").
    if (participantIdFromUrl) {
      if (!validation(participantIdFromUrl, "uuid").isValid) {
        setAttendeeNotFound(true);
        setAttendeeChecking(false);
        return;
      }
    } else if (emailFromUrl) {
      if (!validation(emailFromUrl, "email").isValid) {
        setAttendeeNotFound(true);
        setAttendeeChecking(false);
        return;
      }
    }

    const eventId = template?.eventId || template?.event_id;

    let cancelled = false;
    getAttendeeByIdPublic({
      id: participantIdFromUrl || undefined,
      email: participantIdFromUrl ? undefined : emailFromUrl,
      eventId,
    }).then((res) => {
      if (cancelled) return;
      // 4xx = identificador não corresponde a nenhum participante (ou não
      // pertence ao evento) — bloqueia com tela de aviso. 5xx/sem resposta
      // = problema técnico (fail-safe: deixa o form editável, o submit
      // reporta se der erro).
      if (res && res.status >= 400 && res.status < 500) {
        setAttendeeNotFound(true);
        setAttendeeChecking(false);
        return;
      }
      if (!res || res.status >= 500) {
        setAttendeeChecking(false);
        return;
      }
      const body = res?.data;
      const attendee = body?.data || body || {};
      const nome = attendee.nome || attendee.name || "";
      const email = attendee.email || "";
      // Backend devolve grupos como `grupos: [uuid,...]`. Fallback pra
      // `groupIds` / `groups[].id` cobre variações de contrato.
      const grupos = Array.isArray(attendee.grupos)
        ? attendee.grupos
        : Array.isArray(attendee.groupIds)
          ? attendee.groupIds
          : Array.isArray(attendee.groups)
            ? attendee.groups.map((g) => g?.id || g).filter(Boolean)
            : [];
      setAttendeeGroupIds(grupos);
      if (!nome && !email) {
        setAttendeeChecking(false);
        return;
      }

      setForm((prev) =>
        prev.map((row) => ({
          ...row,
          cols: row.cols.map((col) => {
            const field = (
              col.attributes?.uniqueField ||
              col.attributes?.inputName ||
              ""
            ).toLowerCase();
            if (field === "nome" && nome) {
              return {
                ...col,
                attributes: {
                  ...col.attributes,
                  value: nome,
                  readOnly: true,
                  errorMessage: "",
                },
              };
            }
            if (field === "email" && email) {
              return {
                ...col,
                attributes: {
                  ...col.attributes,
                  value: email,
                  readOnly: true,
                  errorMessage: "",
                },
              };
            }
            return col;
          }),
        })),
      );
      // Força remount do FormMounted pra que os Displays leiam o value
      // inicial (state local desses inputs é inicializado no mount).
      setFormKey((k) => k + 1);
      setAttendeeChecking(false);
    });

    return () => {
      cancelled = true;
    };
  }, [
    participantIdFromUrl,
    emailFromUrl,
    resolvedTypeForm,
    template?.eventId,
    template?.event_id,
    form.length,
  ]);

  // Valida o email digitado no form (não-cadastro sem paxid/email URL):
  // se passar no regex, chama GET /attendees após 800ms de debounce.
  //   - 4xx  → marca errorMessage inline "Este email não está inscrito".
  //   - 200  → limpa qualquer errorMessage externo (deixa o EmailDisplay
  //            controlar o próprio erro de formato).
  // Race safe: cancelled + clearTimeout no cleanup.
  const emailFromFormValue = form
    .flatMap((row) => row.cols)
    .find(
      (col) =>
        (col.attributes?.uniqueField || col.attributes?.inputName || "")
          .toLowerCase() === "email",
    )?.attributes?.value;

  // Se o identificador é o email digitado no form (não veio da URL) e ele
  // fica vazio/inválido, volta ao estado "não identificado" — colunas com
  // groupIds somem novamente até o próximo email válido ser confirmado.
  useEffect(() => {
    if (participantIdFromUrl || emailFromUrl) return;
    if (
      !emailFromFormValue ||
      !validation(emailFromFormValue, "email").isValid
    ) {
      setAttendeeGroupIds(null);
    }
  }, [emailFromFormValue, participantIdFromUrl, emailFromUrl]);

  useEffect(() => {
    if (!resolvedTypeForm || resolvedTypeForm === "cadastro") return;
    if (participantIdFromUrl || emailFromUrl) return;
    if (!emailFromFormValue) return;
    if (!validation(emailFromFormValue, "email").isValid) return;

    const eventId = template?.eventId || template?.event_id;
    let cancelled = false;
    const timer = setTimeout(() => {
      getAttendeeByIdPublic({ email: emailFromFormValue, eventId }).then(
        (res) => {
          if (cancelled) return;
          const notFound = res && res.status >= 400 && res.status < 500;
          if (notFound) {
            // Volta pro estado "não identificado" → colunas com groupIds
            // ficam escondidas de novo.
            setAttendeeGroupIds(null);
          } else if (res && res.status >= 200 && res.status < 300) {
            const body = res?.data;
            const attendee = body?.data || body || {};
            const grupos = Array.isArray(attendee.grupos)
              ? attendee.grupos
              : Array.isArray(attendee.groupIds)
                ? attendee.groupIds
                : Array.isArray(attendee.groups)
                  ? attendee.groups.map((g) => g?.id || g).filter(Boolean)
                  : [];
            setAttendeeGroupIds(grupos);
            // Encadeia o "já respondeu?" — mesma UX do caminho URL: se o
            // participante já respondeu, redireciona pra tela final antes
            // do usuário terminar de preencher. Fail-safe: erro/5xx segue
            // fluxo normal (o handleSubmit re-verifica no envio).
            if (resolvedFormId) {
              checkFormResponsePublic({
                eventId,
                formId: resolvedFormId,
                email: emailFromFormValue,
              }).then((checkRes) => {
                if (cancelled) return;
                if (
                  checkRes?.status < 400 &&
                  checkRes?.data?.answered === true
                ) {
                  setAlreadyAnsweredReason("existing");
                  setAlreadyAnswered(true);
                }
              });
            }
          }
          setForm((prev) =>
            prev.map((row) => ({
              ...row,
              cols: row.cols.map((col) => {
                const field = (
                  col.attributes?.uniqueField ||
                  col.attributes?.inputName ||
                  ""
                ).toLowerCase();
                if (field !== "email") return col;
                // Só interfere se ainda estamos falando do MESMO email que
                // enviamos ao backend — evita sobrescrever validações do
                // próprio EmailDisplay caso o usuário tenha digitado outra
                // coisa entretanto.
                if (col.attributes?.value !== emailFromFormValue) return col;
                if (notFound) {
                  return {
                    ...col,
                    attributes: {
                      ...col.attributes,
                      errorMessage: "Este email não está inscrito neste evento.",
                      useRawErrorMessage: true,
                    },
                  };
                }
                // Encontrado — não altera. O EmailDisplay já limpa o
                // errorMessage próprio quando o formato é válido.
                return col;
              }),
            })),
          );
        },
      );
    }, 800);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    emailFromFormValue,
    resolvedTypeForm,
    participantIdFromUrl,
    emailFromUrl,
    template?.eventId,
    template?.event_id,
    resolvedFormId,
  ]);

  // Feature 2 — verifica se já respondeu (com paxid ou email da URL).
  // Só dispara para forms NÃO-cadastro; para email digitado no form, o
  // check acontece dentro do handleSubmit (evita chamadas a cada digito).
  useEffect(() => {
    if (!resolvedTypeForm || resolvedTypeForm === "cadastro") return;
    if (!resolvedFormId) return;
    const eventId = template?.eventId || template?.event_id;
    if (!eventId) return;
    if (!participantIdFromUrl && !emailFromUrl) return;

    let cancelled = false;
    checkFormResponsePublic({
      eventId,
      formId: resolvedFormId,
      participantId: participantIdFromUrl,
      email: participantIdFromUrl ? undefined : emailFromUrl,
    }).then((res) => {
      if (cancelled) return;
      if (!res || res.status >= 400) return; // fail-safe: não bloqueia
      if (res.data?.answered === true) {
        setAlreadyAnsweredReason("existing");
        setAlreadyAnswered(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    resolvedTypeForm,
    resolvedFormId,
    template?.eventId,
    template?.event_id,
    participantIdFromUrl,
    emailFromUrl,
  ]);


  // Busca o template (opcional — usado só pra mostrar título e validar id).
  useEffect(() => {
    let cancelled = false;
    setTemplateLoading(true);
    axios
      .get(`${urlTemplates}/templates/${id}`)
      .then((res) => {
        if (cancelled) return;
        const body = res?.data;
        setTemplate(
          body?.data?.data || body?.data?.template || body?.data || body,
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setTemplateError(
          err?.response?.status === 401
            ? "Endpoint público de templates ainda não configurado."
            : "Não foi possível carregar o template.",
        );
      })
      .finally(() => !cancelled && setTemplateLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  // ============================================================================
  // Handler de submit — monta payload conforme MOCK.TYPE_FORM e envia
  // ============================================================================
  const handleSubmit = async () => {
    if (submitting) return;

    // 1) Coleta mensagens de erro dos campos (preenchidas pelos Displays).
    // `errorMessage` cobre campos obrigatórios vazios; `inlineError` cobre
    // formato inválido (ex: CPF com dígito verificador errado, e-mail mal
    // formado, etc). Ambos bloqueiam o submit. Usa `visibleForm` — campos
    // escondidos por segregação de grupos não devem bloquear.
    const allCols = visibleForm.flatMap((row) => row.cols);
    const errors = allCols
      .filter(
        (col) => col.attributes?.errorMessage || col.attributes?.inlineError,
      )
      .map((col) => ({
        field: col.attributes.inputName || col.attributes.uniqueField,
        message: col.attributes.errorMessage || col.attributes.inlineError,
      }));

    // Defense-in-depth: garante que o captcha (se existir no form) foi
    // resolvido antes de enviar. Cobre casos em que o useEffect do
    // CaptchaDisplay ainda não populou errorMessage (timing/race).
    const captchaCol = allCols.find(
      (col) =>
        col.attributes?.inputName === "Captcha" ||
        col.attributes?.uniqueField === "Captcha",
    );
    const captchaMissing = captchaCol && !captchaCol.attributes?.value;
    if (captchaMissing) {
      errors.push({
        field: "Captcha",
        message: "Confirme que você não é um robô antes de enviar.",
      });
    }

    if (errors.length > 0) {
      // Só mostra a mensagem específica do captcha quando ele é o único
      // erro pendente — se houver outros campos vazios/inválidos, a
      // mensagem genérica é mais precisa (o usuário precisa olhar toda
      // a tela, não só o captcha).
      const onlyCaptchaMissing = captchaMissing && errors.length === 1;
      setSubmitResult({
        ok: false,
        type: "validation",
        message: onlyCaptchaMissing
          ? "Confirme que você não é um robô antes de enviar."
          : "Verifique os campos destacados em vermelho.",
      });
      return;
    }

    // 2) Upload de arquivos — UploadDisplay guarda value como
    // { files: FileList, path: string }. Antes de montar formColumns,
    // faz upload de cada arquivo e substitui o value pela URL retornada.
    setSubmitting(true);
    setSubmitResult(null);
    try {
      for (const row of visibleForm) {
        for (const col of row.cols) {
          const val = col.attributes?.value;
          const isFileField =
            val && typeof val === "object" && val.files && val.files.length > 0;
          if (isFileField) {
            const fd = new FormData();
            for (const f of val.files) fd.append("files", f);
            const uploadRes = await getUrlFilesPublic(fd);
            const uploadedUrl =
              (Array.isArray(uploadRes) && uploadRes[0]?.url) ||
              uploadRes?.data?.[0]?.url ||
              uploadRes?.url;
            if (!uploadedUrl) {
              setSubmitting(false);
              setSubmitResult({
                ok: false,
                type: "error",
                message: `Falha ao enviar o arquivo do campo "${
                  col.attributes.label || col.attributes.inputName
                }".`,
              });
              return;
            }
            col.attributes.value = uploadedUrl;
          }
        }
      }
    } catch (uploadErr) {
      setSubmitting(false);
      setSubmitResult({
        ok: false,
        type: "error",
        message: "Falha ao enviar arquivos. Tente novamente.",
      });
      return;
    }

    // 3) Monta formColumns só com campos que têm valor (ignora ButtonDisplay etc).
    // attributeId é normalizado pra minúsculo — a API global espera keys
    // padronizadas ("senha", "email", "cpf", etc), não "Senha"/"Email".
    // Usa `visibleForm` pra respeitar a filtragem por grupos — colunas
    // escondidas nunca vão pro payload.
    const formColumns = visibleForm
      .flatMap((row) => row.cols)
      .filter(
        (col) =>
          col.attributes?.value !== undefined && col.attributes.value !== "",
      )
      .map((col) => {
        // Password Input usa columnValue como chave lógica; outros usam
        // uniqueField/inputName. Prioriza columnValue quando existir.
        const rawId = (
          col.attributes.columnValue ||
          col.attributes.uniqueField ||
          col.attributes.inputName ||
          ""
        ).toLowerCase();
        return {
          attributeId: ATTR_ALIASES[rawId] || rawId,
          responseValue: Array.isArray(col.attributes.value)
            ? col.attributes.value.join(";")
            : String(col.attributes.value ?? ""),
        };
      });

    // Se o form estava vinculado a um Status RSVP na configuração, injeta
    // como coluna do payload. O backend lê essa info de dentro do
    // formColumns (padrão das demais colunas).
    if (formIdStatusRsvp) {
      formColumns.push({
        attributeId: "idStatusRsvp",
        responseValue: String(formIdStatusRsvp),
      });
    }

    if (formColumns.length === 0) {
      setSubmitResult({
        ok: false,
        type: "validation",
        message: "Preencha ao menos um campo antes de enviar.",
      });
      return;
    }

    // 3) Monta payload conforme typeForm
    const effectiveTypeForm = resolvedTypeForm || MOCK.TYPE_FORM;
    // eventId vem do template retornado pelo backend. Tenta camelCase e
    // snake_case (não sei qual o backend usa) — fallback pro MOCK só se
    // nenhum vier populado (templates antigos/sistema).
    const templateEventId =
      template?.eventId || template?.event_id || MOCK.EVENT_ID;
    const payload = {
      typeForm: effectiveTypeForm,
      eventId: templateEventId,
      formId: resolvedFormId || MOCK.FORM_ID || id,
      formColumns,
    };

    if (effectiveTypeForm === "cadastro") {
      // Backend do cadastro exige a chave `senha` sempre presente — se o
      // form do editor não incluiu o campo, injeta com valor vazio pra
      // evitar erro no gateway.
      const hasSenha = formColumns.some((c) => c.attributeId === "senha");
      if (!hasSenha) {
        formColumns.push({ attributeId: "senha", responseValue: "" });
      }
      // Cadastro exige formSnapshot (definição do form no momento do submit).
      payload.formSnapshot = {
        version: "1.0",
        title: template?.name || "Template",
      };
    } else {
      // Não-cadastro precisa identificar o participante. Prioridade:
      // 1) paxid da query string (?paxid=<uuid>);
      // 2) email da query string (?email=<email>);
      // 3) email preenchido no formulário — o backend resolve o
      //    participantId a partir do e-mail quando não vier paxid.
      // Chave `email` (não `participantEmail`) — é o que o DTO espera.
      const emailFromForm = formColumns.find(
        (c) => c.attributeId === "email",
      )?.responseValue;
      const resolvedEmail = emailFromUrl || emailFromForm;
      if (participantIdFromUrl) {
        payload.participantId = participantIdFromUrl;
      } else if (resolvedEmail) {
        payload.email = resolvedEmail;
        // Feature 2 — se o identificador é o email digitado no form (não
        // veio na URL), o useEffect de check inicial não rodou. Verifica
        // agora antes do POST. Fail-safe: se der erro, deixa seguir e o
        // backend valida na segunda camada.
        if (!emailFromUrl && emailFromForm) {
          const checkRes = await checkFormResponsePublic({
            eventId: templateEventId,
            formId: resolvedFormId || MOCK.FORM_ID || id,
            email: emailFromForm,
          });
          if (checkRes?.status < 400 && checkRes?.data?.answered === true) {
            setSubmitting(false);
            setAlreadyAnsweredReason("existing");
            setAlreadyAnswered(true);
            return;
          }
        }
      } else {
        setSubmitting(false);
        setSubmitResult({
          ok: false,
          type: "error",
          message:
            "Informe seu e-mail no formulário ou abra o link com ?paxid=<uuid> ou ?email=<email>.",
        });
        return;
      }
    }

    // 4) Envia (setSubmitting já foi ativado no upload).
    const res = await submitFormResponsePublic({ payload });
    const status = res?.status;
    const responseBody = res?.data;
    const success = status && status >= 200 && status < 300;

    if (success) {
      // Não-cadastro (pesquisa etc): mostra a tela "Resposta já registrada"
      // ao invés do SweetAlert — reforça que a jornada terminou e evita
      // o pax responder de novo por engano.
      if (effectiveTypeForm !== "cadastro") {
        setSubmitting(false);
        setAlreadyAnsweredReason("just-submitted");
        setAlreadyAnswered(true);
        return;
      }
      // Cadastro novo → 201 com participantId
      const participantId =
        responseBody?.data?.participantId ||
        responseBody?.participantId ||
        responseBody?.data;
      setSubmitResult({
        ok: true,
        message: "Cadastro realizado com sucesso!",
        participantId,
      });
      // Limpa campos após sucesso (zera values + força remount do FormMounted
      // pra também resetar inputs "não controlados" como <input type="file">).
      setForm((prev) => cleanForm(prev));
      setFormKey((k) => k + 1);
    } else if (status === 409 || responseBody?.alreadyRegistered) {
      // Distingue os dois cenários de conflito retornados pelo backend:
      // CPF_ALREADY_REGISTERED_WITH_DIFFERENT_EMAIL vs email duplicado.
      const errorCode =
        responseBody?.code ||
        responseBody?.errorCode ||
        responseBody?.error ||
        responseBody?.message ||
        "";
      const isCpfConflict =
        typeof errorCode === "string" &&
        errorCode.includes("CPF_ALREADY_REGISTERED");
      setSubmitResult({
        ok: false,
        alreadyRegistered: true,
        message: isCpfConflict
          ? "CPF já cadastrado com outro email."
          : "Email já cadastrado.",
      });
    } else {
      // Mensagens do backend em erros técnicos vêm como strings internas
      // (ex: "API_GLOBAL_ERROR") — não fazem sentido pro usuário final.
      // Usa mensagens amigáveis por faixa de status.
      const isServerUnavailable = status >= 500 && status <= 599;
      setSubmitResult({
        ok: false,
        type: "error",
        message: isServerUnavailable
          ? "Ocorreu um erro. Tente novamente."
          : "Não foi possível enviar sua resposta. Tente novamente.",
      });
    }
    setSubmitting(false);
  };

  // ============================================================================
  // Render
  // ============================================================================
  // Feature 2 — tela final quando o participante já respondeu esse form.
  // Renderiza no lugar do template pra reforçar que a jornada terminou.
  // Feature 1 — paxid da URL inexistente (backend 404). Bloqueia o form
  // e mostra tela centralizada estilo warning: link inválido/expirado.
  if (attendeeNotFound) {
    return (
      <div style={styles.center}>
        <div style={styles.answeredBox}>
          <i
            className="mdi mdi-account-alert-outline"
            style={{ fontSize: 72, color: "#f59e0b", lineHeight: 1 }}
          />
          <h2 style={styles.answeredTitle}>Participante não localizado</h2>
          <p style={styles.answeredText}>
            O link utilizado não corresponde a um participante válido.
            <br />
            Verifique o endereço ou entre em contato com a organização.
          </p>
        </div>
      </div>
    );
  }

  if (alreadyAnswered) {
    // Fase 1 (~800ms): spinner — dá sensação de progresso "processando".
    // Fase 2: mensagem final, com texto diferente pra quem acabou de
    // responder vs pra quem já tinha respondido antes.
    if (!showAnsweredMessage) {
      return (
        <div style={styles.center}>
          <div style={styles.spinner} />
        </div>
      );
    }
    const isJustSubmitted = alreadyAnsweredReason === "just-submitted";
    return (
      <div style={styles.center}>
        <div style={styles.answeredBox}>
          <i
            className="mdi mdi-check-circle"
            style={{ fontSize: 72, color: "#10b981", lineHeight: 1 }}
          />
          <h2 style={styles.answeredTitle}>
            {isJustSubmitted ? "Resposta enviada" : "Resposta já registrada"}
          </h2>
          <p style={styles.answeredText}>
            {isJustSubmitted ? (
              <>
                Sua resposta foi registrada com sucesso.
                <br />
                Obrigado por participar!
              </>
            ) : (
              "Você já respondeu este formulário. Obrigado!"
            )}
          </p>
        </div>
      </div>
    );
  }

  // Inclui `attendeeChecking` pra evitar o "flash" do form aparecer antes
  // do backend confirmar o participante (caso ?paxid=... ou ?email=...).
  if (templateLoading || formLoading || attendeeChecking) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
      </div>
    );
  }

  if (templateError || formError) {
    return (
      <div style={styles.center}>
        <div style={styles.errorBox}>
          <i
            className="mdi mdi-alert-circle-outline"
            style={{ fontSize: 40, color: "#ef4444" }}
          />
          <div style={{ marginTop: 12, color: "#374151" }}>
            {templateError || formError}
          </div>
        </div>
      </div>
    );
  }

  // CSS combinado: FORM_CSS (regras do FormMounted, extraídas de gs.scss) +
  // htmlJson do template (nome histórico, é CSS gerado por editor.getCss()).
  // Mesma ordem/composição do buildPreviewDoc → paridade com "Visualizar
  // → Nova aba" do editor.
  //
  // Fix do modal do LinkButtonDisplay: o Reactstrap 9 (Bootstrap 4 internamente)
  // conflita com o Bootstrap 5.1 que carregamos via CDN pro visual do template.
  // Sem esse override, o modal renderiza com opacity: 0 (invisível).
  //
  // Fix dos radio/checkbox: Bootstrap 5 aplica float+margin negativa em
  // .form-check-input assumindo parent .form-check. Como o Reactstrap
  // renderiza fora desse parent, precisamos neutralizar.
  const templateCss = `${FORM_CSS}\n${template?.htmlJson || ""}\n
    .modal.show { opacity: 1 !important; }
    .modal-backdrop.show { opacity: 0.5 !important; }
    .form-check-input {
      float: none !important;
      margin-left: 0 !important;
      margin-top: 0 !important;
      margin-right: 10px !important;
    }
    /* Wrapper flex de cada opção do RadioDisplay/CheckboxDisplay:
       alinhamento vertical + espaçamento moderado entre opções. */
    [id^="therm-input-component"] > .custom-input-container > div[style*="display: flex"] {
      align-items: center;
      margin: 0 0 8px 0;
    }
    /* Label do checkbox/radio: reset de padding/margin herdado do
       .form-label do FORM_CSS. Pega qualquer classe check-label-*. */
    [class*="check-label-"] {
      padding: 0 !important;
      margin: 0 !important;
      line-height: 1.4 !important;
      align-self: center;
    }
    /* Título da pergunta (span main-question-options-*) — separação da
       primeira opção. */
    [class*="main-question-options-"] {
      display: block;
      margin-bottom: 8px;
    }
  `;

  // Modo estático: template sem form (sem wrapper data-form-container e sem
  // MOCK). Renderiza apenas o htmlString.
  const hasForm = !!resolvedFormId;

  if (!hasForm) {
    return (
      <div style={styles.page}>
        {templateCss && <style>{templateCss}</style>}
        <div dangerouslySetInnerHTML={{ __html: template?.htmlString || "" }} />
      </div>
    );
  }

  // FormMounted vivo — mesmo bloco JSX usado nas duas estratégias.
  const formMountedNode = (
    <FormMounted
      key={formKey}
      form={visibleForm}
      setForm={setForm}
      onSubmit={handleSubmit}
      components={() => null}
    />
  );

  return (
    <div style={styles.page}>
      {templateCss && <style>{templateCss}</style>}
      <div>
        {USE_PORTAL_STRATEGY ? (
          // PORTAL — renderiza o htmlString inteiro (preservando a árvore
          // DOM do modelo) e injeta o form dentro do [data-form-container]
          // via createPortal. Fallback: se o container ainda não foi
          // localizado no DOM, o form é renderizado logo abaixo (não
          // desaparece do usuário durante o mount).
          <>
            <TemplateHtmlWrapper
              html={cleanedHtml}
              wrapperRef={templateWrapperRefCallback}
            />
            {formSlotEl
              ? createPortal(formMountedNode, formSlotEl)
              : (
                <div style={styles.formArea}>{formMountedNode}</div>
              )}
          </>
        ) : (
          <>
            {/* SPLIT (legado) — HTML antes do form + form + HTML depois. */}
            {beforeFormHtml && (
              <div dangerouslySetInnerHTML={{ __html: beforeFormHtml }} />
            )}
            <div style={styles.formArea}>{formMountedNode}</div>
            {afterFormHtml && (
              <div dangerouslySetInnerHTML={{ __html: afterFormHtml }} />
            )}
          </>
        )}

        {submitting && (
          <div style={styles.submitting}>
            <div style={styles.spinner} />
            <span>Enviando...</span>
          </div>
        )}

        {/* Feedback padronizado via SweetAlert:
            - Sucesso              → success (verde).
            - Já cadastrado (409)  → warning (amarelo).
            - Validação client-side → warning (amarelo).
            - Erro técnico          → error (vermelho).
            Mensagens de campo (obrigatório, formato inválido, etc.) continuam
            aparecendo inline abaixo de cada input via FormMounted. */}
        {submitResult?.ok && !submitting && (
          <SweetAlert
            success
            title="Sucesso"
            confirmBtnText="Ok"
            confirmBtnBsStyle="success"
            onConfirm={() => setSubmitResult(null)}
          >
            {submitResult.message}
          </SweetAlert>
        )}

        {submitResult?.alreadyRegistered && !submitting && (
          <SweetAlert
            warning
            title="Atenção"
            confirmBtnText="Ok"
            confirmBtnBsStyle="success"
            onConfirm={() => setSubmitResult(null)}
          >
            {submitResult.message}
          </SweetAlert>
        )}

        {submitResult?.type === "validation" && !submitting && (
          <SweetAlert
            warning
            title="Atenção"
            confirmBtnText="Ok"
            confirmBtnBsStyle="success"
            onConfirm={() => setSubmitResult(null)}
          >
            {submitResult.message}
          </SweetAlert>
        )}

        {submitResult?.type === "error" && !submitting && (
          <SweetAlert
            error
            title="Erro"
            confirmBtnText="Ok"
            confirmBtnBsStyle="success"
            onConfirm={() => setSubmitResult(null)}
          >
            {submitResult.message}
          </SweetAlert>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: {
    // Modo edge-to-edge (opção B) — paridade com "Visualizar → Nova aba".
    // O CSS do template controla background/padding próprios.
    minHeight: "100vh",
    background: "#fff",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  // Padding lateral só na área do form (hero fica edge-to-edge).
  formArea: {
    padding: "0 24px",
  },
  card: {
    maxWidth: 720,
    margin: "0 auto",
    background: "#fff",
    padding: 32,
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
    border: "1px solid #e6e8eb",
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: "#040050",
    marginBottom: 24,
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100vw",
    height: "100vh",
    background: "#fff",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  answeredBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    maxWidth: 480,
    padding: "40px 32px",
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #e6e8eb",
    boxShadow: "0 8px 32px rgba(16, 185, 129, 0.08)",
  },
  answeredTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#111827",
    marginTop: 20,
    marginBottom: 8,
  },
  answeredText: {
    fontSize: 15,
    lineHeight: 1.6,
    color: "#4b5563",
    margin: 0,
  },
  spinner: {
    width: 32,
    height: 32,
    border: "4px solid #e5e7eb",
    borderLeftColor: "#040050",
    borderRadius: "50%",
    animation: "public-spin 0.8s linear infinite",
  },
  errorBox: {
    textAlign: "center",
    maxWidth: 400,
    padding: 32,
  },
  submitting: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 16,
    color: "#6b7280",
  },
  result: {
    marginTop: 16,
    padding: "12px 16px",
    borderRadius: 8,
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    fontSize: 14,
  },
};

// Keyframes globais.
if (
  typeof document !== "undefined" &&
  !document.getElementById("public-spin")
) {
  const s = document.createElement("style");
  s.id = "public-spin";
  s.textContent =
    "@keyframes public-spin { to { transform: rotate(360deg); } }";
  document.head.appendChild(s);
}

export default PublicTemplate;
